import { AreaViewIcon, makeStyles, SurfaceViewIcon, useTranslation } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import {
  cleanUpWorkOrderView,
  getItems,
  getObjectLabels,
  getQuoteOptions,
  getWorkOrderHiddenItems,
  getWorkOrderViewHasCustomizations,
  getWorkOrderViewItems,
  setQuoteOptions
} from '@paintscout/util/builder'
import React, { useEffect, useRef, useState } from 'react'
import { useQuote } from '../context'
import MediaSectionNext from './MediaSectionNext'
import QuoteDocumentHeader from './QuoteDocumentHeader/QuoteDocumentHeader'
import WorkOrderSectionNext from './WorkOrderSectionNext'
import WorkOrderSummaryNext from './WorkOrderSummaryNext'
import type { WorkOrderSummaryNextHandle } from './WorkOrderSummaryNext'
import OptionsTableNext from './OptionsTableNext'
import WorkOrderNavBarNext from './WorkOrderNavBarNext'
import type { WorkOrderNavSection, WorkOrderSectionFilter } from './WorkOrderNavBarNext'
import WorkOrderHiddenItemsNext from './WorkOrderHiddenItemsNext'
import { useWorkOrderSectionFilter } from './workOrderSectionFilterPreference'

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    const border = `1px solid ${neutral.P90}`
    return {
      root: {
        marginBottom: 20
      },
      // The document sheet — a single bordered white card (matches the estimate document), holding the
      // flush header + summary + sections.
      documentCard: {
        border,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: neutral.White,
        overflow: 'hidden',
        marginBottom: theme.spacing(2),
        '@media print': { border: 'none' },
        // Phones open the sheet up, as the prototype does (ENG-2080): the header + summary become their
        // own rounded card (`summaryCard`), and the sections sit below it on the page.
        [theme.breakpoints.down('xs')]: {
          border: 'none',
          borderRadius: 0,
          backgroundColor: 'transparent',
          overflow: 'visible'
        }
      },
      summaryCard: {
        [theme.breakpoints.down('xs')]: {
          border: `0.8px solid ${neutral.P90}`,
          borderRadius: 10,
          backgroundColor: neutral.White,
          overflow: 'hidden'
        }
      },
      // Areas / Surfaces live on the same white sheet as the header + summary, below a divider.
      // The nav bar sits at the top with its own (small) bottom margin, so this container itself has
      // no gap; the section groups are gapped by `sectionsList` below.
      sectionsBody: {
        borderTop: border,
        // 20px, matching the mock's sections block (the summary above it runs a wider 28px).
        padding: theme.spacing(2.5),
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.down('xs')]: {
          borderTop: 'none',
          padding: theme.spacing(2.5, 0, 0)
        }
      },
      sectionsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        marginTop: theme.spacing(2)
      },
      // Full-bleed rule above Options (ENG-1850) — negative margins cancel `sectionsBody`'s padding
      // so it spans the whole card edge-to-edge, with extra breathing room on top of it.
      optionsDivider: {
        borderTop: border,
        margin: theme.spacing(2, -2.5, 0, -2.5),
        [theme.breakpoints.down('xs')]: {
          marginLeft: 0,
          marginRight: 0
        },
        '@media print': { display: 'none' }
      }
    }
  },
  { name: 'QuoteNextWorkOrderView' }
)

/**
 * The redesigned Work Order tab for the new estimate editor (estimateNext). Mirrors the estimate
 * document: a single sheet with the shared `QuoteDocumentHeader` (work-order variant) on top, then the
 * two-column `WorkOrderSummaryNext`, then the nav bar + the Area / Surface / Options card sections and
 * the unified Hidden items panel, and Media last.
 */
export default function QuoteNextWorkOrderView() {
  const classes = useStyles()
  const { quote, tableView, isEditable, updateQuote, onItemAction, canAddAdditionalWork } = useQuote()
  const { options } = useClientOptions()
  const { __, lang } = useTranslation()

  const summaryRef = useRef<WorkOrderSummaryNextHandle>(null)

  // Which card sections are on screen, and whether the Hidden items section is expanded. UI state
  // only — deliberately not persisted onto the quote. The filter is remembered per browser so it
  // survives tab switches and reloads (ENG-2003).
  const [sectionFilter, setSectionFilter] = useWorkOrderSectionFilter()
  const [hiddenOpen, setHiddenOpen] = useState(false)

  const objectLabels = getObjectLabels({ options })

  // Which hour-visibility flags drive each section's cards, plus the "Show all hours" dropdown
  // handler (mirrors the legacy WorkOrderView.handleToggleAmount — sets the item/total hour flags).
  const {
    showWorkOrderAreaValues,
    showWorkOrderSubstrateValues,
    showWorkOrderTotalHours,
    showWorkOrderAreaOptions,
    showWorkOrderFiles,
    workOrderOptionsConsumer
  } = getQuoteOptions({ quote, options })

  const handleToggleAmount = (dropdownOption: string) => {
    const next = getQuoteOptions({ options, quote })
    const itemKeys = [
      'showWorkOrderAreaValues',
      'showWorkOrderSubstrateValues',
      'showWorkOrderAreaOptionValues',
      'showWorkOrderSubstrateOptionValues'
    ]
    const totalKeys = ['showWorkOrderTotalHours']
    let trueKeys: string[] = []
    let falseKeys: string[] = []
    if (dropdownOption === 'showAllHours') {
      trueKeys = [...itemKeys, ...totalKeys]
    } else if (dropdownOption === 'hideAllHours') {
      falseKeys = [...itemKeys, ...totalKeys]
    } else if (dropdownOption === 'hideItemHours') {
      falseKeys = [...itemKeys]
      trueKeys = [...totalKeys]
    } else if (dropdownOption === 'hideTotalHours') {
      trueKeys = [...itemKeys]
      falseKeys = [...totalKeys]
    }
    trueKeys.forEach((key) => {
      next[key] = true
    })
    falseKeys.forEach((key) => {
      next[key] = false
    })
    updateQuote({ quote: setQuoteOptions({ quoteOptions: next, quote }) })
  }

  const handleSectionItemClick = (_ev: React.MouseEvent<HTMLElement>, key: string) => {
    onItemAction?.('edit', 'bid', [key])
  }

  const handleAdditionalItemClick = (_ev: React.MouseEvent<HTMLElement>, key: string) => {
    onItemAction?.('edit', 'additional', [key])
  }

  // Toggle the product lines on every card *and* the summary's Products block (nav-bar action,
  // persisted per quote). Hiding unmounts the Products block, so an open inline edit is committed onto
  // the same quote we're about to write — one update, no clobbering.
  const handleToggleProducts = () => {
    const baseQuote = summaryRef.current?.commitOpenProducts(quote) ?? quote
    const next = getQuoteOptions({ options, quote: baseQuote })
    next.showWorkOrderProducts = next.showWorkOrderProducts === false
    updateQuote({ quote: setQuoteOptions({ quoteOptions: next, quote: baseQuote }) })
  }

  // Toggle the Options section's show/hide (its own WO option), matching the estimate's Hide pill.
  const handleToggleOptions = () => {
    const next = getQuoteOptions({ options, quote })
    next.showWorkOrderAreaOptions = !next.showWorkOrderAreaOptions
    updateQuote({ quote: setQuoteOptions({ quoteOptions: next, quote }) })
  }

  // Switches the Options card between customer prices and crew hours (mirrors the legacy
  // WorkOrderView's Customer/Price vs Crew/Hours switcher).
  const handleToggleOptionsConsumer = (view: 'crew' | 'customer') => {
    const next = getQuoteOptions({ options, quote })
    next.workOrderOptionsConsumer = view
    updateQuote({ quote: setQuoteOptions({ quoteOptions: next, quote }) })
  }

  // Toggle the Media section, the same per-quote option the legacy work order used.
  const handleToggleFiles = () => {
    const next = getQuoteOptions({ options, quote })
    next.showWorkOrderFiles = !next.showWorkOrderFiles
    updateQuote({ quote: setQuoteOptions({ quoteOptions: next, quote }) })
  }

  // Whether Options will render anything (mirrors OptionsTableNext's own empty guard) — gates the
  // divider above it, so the rule doesn't show above an empty/hidden section (ENG-1850).
  const hasOptionsItems =
    canAddAdditionalWork ||
    getItems({ quote, section: 'options', view: tableView, consumer: 'customer', options }).length > 0

  // Nav-pill presence: a pill per view that has content, prefixed with All when there's a choice to
  // make. Options isn't a pill — it always renders below whatever is showing. Hidden count spans both
  // views.
  const hasAreaItems = getWorkOrderViewItems({ quote, options, view: 'area', lang }).length > 0
  const hasSubstrateItems = getWorkOrderViewItems({ quote, options, view: 'substrate', lang }).length > 0
  const hidden = getWorkOrderHiddenItems({ quote, options, lang })
  const viewSections: WorkOrderNavSection[] = [
    hasAreaItems && { key: 'area' as const, label: __('Areas'), Icon: AreaViewIcon },
    hasSubstrateItems && { key: 'substrate' as const, label: __(objectLabels.substrate.plural), Icon: SurfaceViewIcon }
  ].filter(Boolean) as WorkOrderNavSection[]
  const navSections: WorkOrderNavSection[] =
    viewSections.length > 1 ? [{ key: 'all', label: __('All') }, ...viewSections] : viewSections

  // Keep the filter on something that exists: with one view there's nothing to filter, and a view can
  // lose its last card while selected.
  const activeFilter: WorkOrderSectionFilter = navSections.some((section) => section.key === sectionFilter)
    ? sectionFilter
    : navSections[0]?.key ?? 'all'
  const showAreas = activeFilter === 'all' || activeFilter === 'area'
  const showSubstrates = activeFilter === 'all' || activeFilter === 'substrate'

  // Restoring the last hidden card takes its button away with it, so drop the expanded state too —
  // otherwise hiding something later would pop the section open on its own.
  useEffect(() => {
    if (hidden.total === 0) {
      setHiddenOpen(false)
    }
  }, [hidden.total])

  // Clean up follows the active section in the nav bar — a handler exists only for a rearranged/resized view.
  const makeCleanUp = (view: 'area' | 'substrate') =>
    getWorkOrderViewHasCustomizations(quote, view)
      ? () => updateQuote({ quote: cleanUpWorkOrderView({ quote, view }) })
      : undefined
  const cleanUp = { area: makeCleanUp('area'), substrate: makeCleanUp('substrate') }

  return (
    <div className={classes.root} data-testid="work-order-next">
      <div className={classes.documentCard}>
        <div className={classes.summaryCard}>
          <QuoteDocumentHeader flush variant="work-order" />

          <WorkOrderSummaryNext ref={summaryRef} />
        </div>

        {/* Top nav bar (All/Areas/Surfaces filter pills + Hidden items + Hide all products + global
            Show all hours), then the sections the filter selects. Each section returns null when it
            has no items; per-card hide/width live on the cards, and Options always renders last. */}
        <div className={classes.sectionsBody}>
          {isEditable && (
            <WorkOrderNavBarNext
              sections={navSections}
              value={activeFilter}
              onChange={setSectionFilter}
              hiddenCount={hidden.total}
              hiddenOpen={hiddenOpen}
              onToggleHidden={() => setHiddenOpen((prev) => !prev)}
              cleanUp={cleanUp}
              onToggleAmount={handleToggleAmount}
              onToggleProducts={handleToggleProducts}
            />
          )}

          <div className={classes.sectionsList}>
            {/* Hidden items sits above the cards, and only while its nav-bar button is toggled on. */}
            {hiddenOpen && <WorkOrderHiddenItemsNext />}

            {showAreas && (
              <>
                <WorkOrderSectionNext
                  title={__('Areas')}
                  tableView="area"
                  showHours={showWorkOrderAreaValues}
                  showTotalHours={showWorkOrderTotalHours}
                  onItemClick={handleSectionItemClick}
                />
                <WorkOrderSectionNext
                  title={__(objectLabels.additionalWork.value)}
                  section="additional"
                  tableView="area"
                  showHours={showWorkOrderAreaValues}
                  showTotalHours={showWorkOrderTotalHours}
                  showTitle={false}
                  onItemClick={handleAdditionalItemClick}
                />
              </>
            )}

            {showSubstrates && (
              <>
                <WorkOrderSectionNext
                  title={__(objectLabels.substrate.plural)}
                  tableView="substrate"
                  showHours={showWorkOrderSubstrateValues}
                  showTotalHours={showWorkOrderTotalHours}
                  onItemClick={handleSectionItemClick}
                />
                <WorkOrderSectionNext
                  title={__(objectLabels.additionalWork.value)}
                  section="additional"
                  tableView="substrate"
                  showHours={showWorkOrderSubstrateValues}
                  showTotalHours={showWorkOrderTotalHours}
                  showTitle={false}
                  onItemClick={handleAdditionalItemClick}
                />
              </>
            )}

            {/* Full-width divider between the Areas/Surfaces/Additional Work above and Options. */}
            {hasOptionsItems && <div className={classes.optionsDivider} />}

            {/* Options render in-card in the new outlined style (shared with the estimate). */}
            <OptionsTableNext
              collapsed={!showWorkOrderAreaOptions}
              onToggle={handleToggleOptions}
              showAddButton={isEditable || canAddAdditionalWork}
              onItemAction={(action, section, item) => onItemAction?.(action, section, [item.key])}
              inWorkOrder
              consumer={workOrderOptionsConsumer}
              onConsumerChange={handleToggleOptionsConsumer}
            />

            {/* Media, in the same card the estimate uses — design dropped the icon + bare heading it
                started with. This replaces the legacy `WorkOrderView` mount whose every other block
                was suppressed — Media was all it still rendered. */}
            <MediaSectionNext variant="work-order" collapsed={!showWorkOrderFiles} onToggle={handleToggleFiles} />
          </div>
        </div>
      </div>
    </div>
  )
}
